import { useEffect, useMemo, useRef, useState } from 'react';

export type UseTickerOptions = {
  startAt?: number;
  onDone?: () => void;
};

type TickerIntervalArg = number | ((arg0: number) => number);

export const useTicker = (
  interval: TickerIntervalArg,
  stopAtTick: number,
  tickerOpts: UseTickerOptions | undefined = undefined
) => {
  const [config, setConfig] = useState<{
    interval: TickerIntervalArg;
    stopAtTick: number;
    startAt: number;
  }>({ interval, stopAtTick, startAt: tickerOpts?.startAt ?? 0 });

  const intervalFn = useMemo<(arg0: number) => number>(
    () =>
      typeof config.interval === 'number'
        ? () => config.interval as number
        : config.interval,
    [config.interval]
  );

  const [done, setDone] = useState(false);
  const [tick, setTick] = useState(config.startAt);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startAt = tickerOpts?.startAt ?? 0;

  // Hold onDone in a ref so a fresh tickerOpts identity with unchanged values
  // doesn't restart the ticker — the reset effect depends on primitives only.
  const onDoneRef = useRef(tickerOpts?.onDone);
  useEffect(() => {
    onDoneRef.current = tickerOpts?.onDone;
  }, [tickerOpts?.onDone]);

  useEffect(() => {
    setConfig({ interval, stopAtTick, startAt });
    setTick(startAt);
    setDone(false);
  }, [interval, stopAtTick, startAt]);

  useEffect(() => {
    if (tick === config.stopAtTick) {
      setDone(true);
      onDoneRef.current?.();
    }
  }, [tick, config.stopAtTick]);

  useEffect(() => {
    if (!done) {
      timerRef.current = setTimeout(
        () =>
          setTick((previousTick) =>
            Math.min(previousTick + 1, config.stopAtTick)
          ),
        intervalFn(tick)
      );
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [tick, config.stopAtTick, intervalFn, done]);

  return { tick, done };
};

export const useLoopingTicker = (delay: number, max: number, loop = true) => {
  const [tick, setTick] = useState(0);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isActive) {
        setTick((current) => {
          if (current === max) {
            if (loop) {
              return 0; // Loop back to start
            }
            setIsActive(false); // Stop the animation
            return current; // Stay at the last frame
          }
          return current + 1;
        });
      }
    }, delay);
    return () => clearInterval(interval);
  }, [delay, isActive, max, loop]);

  return tick;
};
